import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  SelectContainer,
  SelectLabel,
  SelectButton,
  SelectIcon,
  DropdownList,
  DropdownOption,
  ErrorMessage,
  CheckboxWrapper,
  HiddenCheckbox,
  StyledCheckbox
} from './styled'
import { ChevronDown } from '../../../assets/icons'

const MultiSelect = ({
  label,
  name,
  value,
  onChange,
  options = [],
  placeholder = 'Seleccionar...',
  error,
  disabled = false,
  required = false,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef(null)

  const selectedValues = Array.isArray(value) ? value : []
  
  const getDisplayText = () => {
    if (selectedValues.length === 0) return placeholder
    
    const selectedOptions = options.filter(opt => selectedValues.includes(opt.value))
    
    if (selectedValues.length <= 2) {
      return selectedOptions.map(opt => opt.label).join(', ')
    }
    
    const firstTwo = selectedOptions.slice(0, 2).map(opt => opt.label).join(', ')
    const remaining = selectedValues.length - 2
    return `${firstTwo} +${remaining}`
  }
  
  const displayText = getDisplayText()

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false)
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
    }
  }

  const handleSelect = (option) => {
    const currentValues = Array.isArray(value) ? value : []
    const isSelected = currentValues.includes(option.value)
    const newValues = isSelected
      ? currentValues.filter(v => v !== option.value)
      : [...currentValues, option.value]
    
    const syntheticEvent = {
      target: {
        name: name,
        value: newValues
      }
    }
    onChange(syntheticEvent)
  }

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <SelectContainer>
        {label && (
          <SelectLabel htmlFor={name}>
            {label}
            {required && <span style={{ color: '#D32F2F' }}> *</span>}
          </SelectLabel>
        )}

        <div style={{ position: 'relative' }}>
          <SelectButton
            type="button"
            id={name}
            onClick={handleToggle}
            disabled={disabled}
            $error={!!error}
            $isOpen={isOpen}
            $hasValue={selectedValues.length > 0}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            {...props}
          >
            <span>{displayText}</span>
            <SelectIcon $isOpen={isOpen}>
              <ChevronDown />
            </SelectIcon>
          </SelectButton>

          {isOpen && (
            <DropdownList>
              {options.map((option) => {
                const isSelected = (Array.isArray(value) ? value : []).includes(option.value)
                return (
                  <DropdownOption
                    key={option.value}
                    onClick={() => handleSelect(option)}
                    $isSelected={isSelected}
                  >
                    <CheckboxWrapper>
                      <HiddenCheckbox
                        checked={isSelected}
                        onChange={() => {}}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <StyledCheckbox $checked={isSelected}>
                        {isSelected && (
                          <svg viewBox="0 0 24 24">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                          </svg>
                        )}
                      </StyledCheckbox>
                      <span>{option.label}</span>
                    </CheckboxWrapper>
                  </DropdownOption>
                )
              })}
            </DropdownList>
          )}
        </div>

        {error && <ErrorMessage>{error}</ErrorMessage>}
      </SelectContainer>
    </div>
  )
}

MultiSelect.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool
}

export default MultiSelect
